if (!customElements.get('collection-infinite-scroll')) {
  customElements.define(
    'collection-infinite-scroll',
    class CollectionInfiniteScroll extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.grid = this.querySelector('#product-grid');
        this.controls = this.querySelector('[data-infinite-controls]');
        this.button = this.querySelector('[data-infinite-button]');
        this.status = this.querySelector('[data-infinite-status]');
        this.fallback = this.querySelector('[data-infinite-fallback]');
        if (!this.grid || !this.controls || !this.dataset.nextUrl || !window.fetch || !window.AbortController) return;

        this.initialized = true;
        this.paused = false;
        this.loading = false;
        this.page = Number(this.dataset.page);
        this.context = window.location.pathname + window.location.search;
        this.connection = navigator.connection;
        this.automatic = !this.connection?.saveData && !['slow-2g', '2g', '3g'].includes(this.connection?.effectiveType);
        this.onButtonClick = (event) => this.loadMore(true, event.detail === 0);
        this.onPageHide = () => {
          this.savePosition();
          this.controller?.abort('navigation');
        };
        this.onPageShow = (event) => {
          if (event.persisted) this.observe();
        };
        this.onProductClick = (event) => {
          const link = event.target.closest('a[href]');
          if (link && this.grid.contains(link) && new URL(link.href).pathname.includes('/products/')) this.savePosition();
        };
        this.onConnectionChange = () => {
          if (this.connection?.saveData || ['slow-2g', '2g', '3g'].includes(this.connection?.effectiveType)) {
            this.automatic = false;
            this.observer?.disconnect();
          }
        };

        this.button.addEventListener('click', this.onButtonClick);
        this.grid.addEventListener('click', this.onProductClick);
        window.addEventListener('pagehide', this.onPageHide);
        window.addEventListener('pageshow', this.onPageShow);
        this.connection?.addEventListener('change', this.onConnectionChange);

        this.restorePosition();
        this.controls.hidden = false;
        this.setAttribute('data-enhanced', '');
        this.button.hidden = !this.dataset.nextUrl;
        if (!this.dataset.nextUrl) this.status.textContent = this.dataset.completeText;

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              if (entries.some((entry) => entry.isIntersecting)) this.loadMore();
            },
            { rootMargin: `0px 0px ${Math.min(1800, Math.max(600, window.innerHeight * 1.5))}px 0px` }
          );
          this.observe();
        }
      }

      disconnectedCallback() {
        this.controller?.abort();
        this.observer?.disconnect();
        clearTimeout(this.timeout);
        this.button?.removeEventListener('click', this.onButtonClick);
        this.grid?.removeEventListener('click', this.onProductClick);
        window.removeEventListener('pagehide', this.onPageHide);
        window.removeEventListener('pageshow', this.onPageShow);
        this.connection?.removeEventListener('change', this.onConnectionChange);
        this.initialized = false;
      }

      observe() {
        if (this.automatic && !this.paused && !this.loading && this.dataset.nextUrl && this.isConnected) {
          this.observer?.observe(this.querySelector('[data-infinite-sentinel]'));
        }
      }

      pause() {
        this.paused = true;
        this.controller?.abort();
        this.observer?.disconnect();
        if (this.button) this.button.disabled = true;
      }

      showFilterError() {
        if (!this.initialized) return;
        this.status.textContent = this.dataset.errorText;
        this.fallback.href = window.location.href;
        this.fallback.hidden = false;
        this.button.hidden = true;
        this.closest('#ProductGridContainer')?.querySelector('.collection')?.classList.remove('loading');
      }

      async loadMore(manual = false, moveFocus = false) {
        if (this.loading || this.paused || !this.dataset.nextUrl || (!manual && !this.automatic)) return;
        if (!navigator.onLine) {
          this.showError();
          return;
        }

        const url = new URL(window.location.href);
        const nextUrl = new URL(this.dataset.nextUrl, window.location.href);
        if (nextUrl.origin !== url.origin || nextUrl.pathname !== url.pathname) {
          this.showError();
          return;
        }
        url.searchParams.set('page', nextUrl.searchParams.get('page'));
        url.searchParams.set('section_id', this.dataset.sectionId);
        const expectedPage = Number(url.searchParams.get('page'));
        const controller = new AbortController();
        const startedAt = performance.now();
        this.controller = controller;
        this.loading = true;
        this.observer?.disconnect();
        this.grid.setAttribute('aria-busy', 'true');
        this.button.disabled = true;
        this.fallback.hidden = true;
        this.status.textContent = this.dataset.loadingText;
        this.timeout = setTimeout(() => controller.abort(), 20000);

        try {
          const response = await fetch(url.href, { signal: controller.signal });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          if (!this.isConnected || this.paused || controller.signal.aborted) return;
          const sourceGrid = html.getElementById('product-grid');
          const source = html.querySelector('collection-infinite-scroll');
          if (
            !sourceGrid ||
            sourceGrid.dataset.id !== this.dataset.sectionId ||
            Number(sourceGrid.dataset.page) !== expectedPage ||
            expectedPage <= this.page ||
            !source ||
            !sourceGrid.children.length
          ) {
            throw new Error('Invalid collection page');
          }

          const existingIds = new Set(Array.from(this.grid.children, (item) => item.dataset.productId));
          const items = Array.from(sourceGrid.children).filter((item) => !existingIds.has(item.dataset.productId));
          const fragment = document.createDocumentFragment();
          items.forEach((item) => {
            item.querySelectorAll('img').forEach((image) => {
              image.loading = 'lazy';
              image.removeAttribute('fetchpriority');
            });
            item.classList.add('scroll-trigger--cancel');
            fragment.appendChild(item);
          });
          this.grid.appendChild(fragment);
          this.page = expectedPage;
          this.dataset.page = String(this.page);
          this.dataset.nextUrl = source.dataset.nextUrl || '';
          this.fallback.href = this.dataset.nextUrl || window.location.href;
          this.querySelector('[data-infinite-label]').hidden = false;
          this.querySelector('[data-infinite-retry]').hidden = true;
          this.status.textContent = this.dataset.nextUrl
            ? this.dataset.loadedText.replace('[count]', String(items.length))
            : this.dataset.completeText;
          this.button.hidden = !this.dataset.nextUrl;

          if (performance.now() - startedAt >= 3500) {
            this.automatic = false;
            if (this.dataset.nextUrl) this.status.textContent += ` ${this.dataset.slowText}`;
          }
          if (moveFocus && items.length) {
            const target = Array.from(items[0].querySelectorAll('a[href]')).find(
              (link) => link.getClientRects().length && getComputedStyle(link).visibility !== 'hidden'
            );
            target?.focus({ preventScroll: true });
            items[0].scrollIntoView({ block: 'nearest' });
          }
        } catch (error) {
          if (this.isConnected && !this.paused && controller.signal.reason !== 'navigation') this.showError();
        } finally {
          clearTimeout(this.timeout);
          this.loading = false;
          this.grid.setAttribute('aria-busy', 'false');
          if (!this.paused) this.button.disabled = false;
          this.observe();
        }
      }

      showError() {
        this.automatic = false;
        this.observer?.disconnect();
        this.status.textContent = this.dataset.errorText;
        this.querySelector('[data-infinite-label]').hidden = true;
        this.querySelector('[data-infinite-retry]').hidden = false;
        this.fallback.href = this.dataset.nextUrl;
        this.fallback.hidden = false;
      }

      savePosition() {
        if (!this.initialized || this.paused || this.page <= Number(this.grid.dataset.page)) return;
        const html = this.grid.innerHTML;
        try {
          sessionStorage.removeItem('dawn:collection-scroll');
          if (html.length > 1000000) return;
          sessionStorage.setItem(
            'dawn:collection-scroll',
            JSON.stringify({
              context: window.location.pathname + window.location.search,
              sectionId: this.dataset.sectionId,
              catalogMode: this.dataset.catalogMode,
              html,
              page: this.page,
              nextUrl: this.dataset.nextUrl,
              scrollY: window.scrollY,
              savedAt: Date.now(),
            })
          );
        } catch (error) {
          return;
        }
      }

      restorePosition() {
        if (performance.getEntriesByType('navigation')[0]?.type !== 'back_forward') return;
        try {
          const saved = JSON.parse(sessionStorage.getItem('dawn:collection-scroll'));
          if (
            !saved ||
            saved.context !== this.context ||
            saved.sectionId !== this.dataset.sectionId ||
            saved.catalogMode !== this.dataset.catalogMode ||
            Date.now() - saved.savedAt > 15 * 60 * 1000
          ) return;
          this.grid.innerHTML = saved.html;
          this.page = saved.page;
          this.dataset.page = String(saved.page);
          this.dataset.nextUrl = saved.nextUrl;
          this.grid.querySelectorAll('.scroll-trigger').forEach((item) => item.classList.add('scroll-trigger--cancel'));
          requestAnimationFrame(() => window.scrollTo({ top: saved.scrollY, behavior: 'instant' }));
        } catch (error) {
          return;
        }
      }
    }
  );
}
